/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  benefitListResponse,
  createBenefitPayload,
  updateBenefitPayload,
} from 'interfaces/Benefit';
import { errorRedux } from 'interfaces/redux';
import {
  createBenefit,
  deleteBenefit,
  getBenefitById,
  getBenefitList,
  updateBenefit,
} from './service';

// Action: Listar benefícios
export const benefitList = createAsyncThunk<
  benefitListResponse,
  undefined,
  { rejectValue: errorRedux }
>('benefit/list', async (_, { rejectWithValue }) => {
  try {
    const data = await getBenefitList();
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar benefício por ID
export const benefitById = createAsyncThunk<
  benefitListResponse,
  number,
  { rejectValue: errorRedux }
>('benefit/getById', async (id, { rejectWithValue }) => {
  try {
    const data = await getBenefitById(id);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Criar benefício
export const benefitCreate = createAsyncThunk<
  benefitListResponse,
  createBenefitPayload,
  { rejectValue: errorRedux }
>('benefit/create', async (payload, { rejectWithValue }) => {
  try {
    const data = await createBenefit(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Atualizar benefício
export const benefitUpdate = createAsyncThunk<
  benefitListResponse,
  updateBenefitPayload,
  { rejectValue: errorRedux }
>('benefit/update', async (payload, { rejectWithValue }) => {
  try {
    const data = await updateBenefit(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Deletar benefício
export const benefitDelete = createAsyncThunk<void, number, { rejectValue: errorRedux }>(
  'benefit/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deleteBenefit(id);
      return undefined;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Reducer
interface IBenefit extends errorRedux {
  benefitList: benefitListResponse;
  benefitById: benefitListResponse;
}

const initialState: IBenefit = {
  status: 'idle',
  message: '',
  benefitList: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  benefitById: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
};

export const BenefitReducer = createSlice({
  name: 'BenefitStatus',
  initialState,
  reducers: {
    clearBenefitStatus: (state) => {
      state.status = 'idle';
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(benefitList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(benefitList.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.benefitList = action.payload;
      })
      .addCase(benefitList.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(benefitById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(benefitById.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.benefitById = action.payload;
      })
      .addCase(benefitById.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(benefitCreate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(benefitCreate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(benefitCreate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(benefitUpdate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(benefitUpdate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(benefitUpdate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(benefitDelete.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(benefitDelete.fulfilled, (state) => {
        state.status = 'completed';
        state.message = 'Benefício deletado com sucesso';
      })
      .addCase(benefitDelete.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      });
  },
});

export const { clearBenefitStatus } = BenefitReducer.actions;
export default BenefitReducer.reducer;
